// frontend/src/screens/ResetPassword.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword } from '../services/authApi';
import { localizedServerContent, localizedValue } from '../localization/presentationLanguage';

const copy = (es, en) => localizedValue({ es, en });

const ResetPassword = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const token = useMemo(() => (new URLSearchParams(search).get('token') || '').trim(), [search]);

  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [msg, setMsg]   = useState('');
  const [ok, setOk]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!token) { setMsg(copy('Falta el token. Volvé a solicitar el email.', 'The token is missing. Request another email.')); return; }
    if (!pwd1 || !pwd2) { setMsg(copy('Completá ambas contraseñas.', 'Complete both password fields.')); return; }
    if (pwd1 !== pwd2) { setMsg(copy('Las contraseñas no coinciden.', 'The passwords do not match.')); return; }
    if (pwd1.length < 6) { setMsg(copy('Mínimo 6 caracteres.', 'Use at least 6 characters.')); return; }

    try {
      setLoading(true);
      await resetPassword(token, pwd1);
      setOk(true);
      setMsg(copy('¡Listo! Tu contraseña fue actualizada.', 'Done! Your password was updated.'));
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setOk(false);
      setMsg(localizedServerContent(err?.message, copy('No se pudo restablecer la contraseña.', 'The password could not be reset.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}>
          <button type="button" style={S.backBtn} onClick={() => navigate(-1)}>
            ← {copy('Volver', 'Back')}
          </button>
          <h1 style={S.title}>{copy('Restablecer contraseña', 'Reset password')}</h1>
          <div style={{ width: 80 }} />
        </div>

        {!token && (
          <div style={S.error}>
            {copy('Token ausente o inválido. Volvé a', 'The token is missing or invalid. Return to')}{' '}
            <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/forgot-password')}>
              Solicitar
            </span>.
          </div>
        )}

        {msg && (
          <div style={{ ...S.alert, background: ok ? '#e8f7ee' : '#fdecea', color: ok ? '#146c2e' : '#b3261e' }}>
            {msg}
          </div>
        )}

        <form onSubmit={onSubmit} style={S.form} autoComplete="on">
          <label htmlFor="newPassword" style={S.label}>{copy('Nueva contraseña', 'New password')}</label>
          <div style={{ position: 'relative' }}>
            <input
              id="newPassword"
              name="new-password"
              type={showPwd ? 'text' : 'password'}
              autoComplete="new-password"
              style={S.input}
              value={pwd1}
              onChange={(e) => setPwd1(e.target.value)}
              disabled={!token || loading}
              required
            />
            <span
              onClick={() => setShowPwd(p => !p)}
              style={S.toggle}
              role="button"
              aria-label={copy('Mostrar/ocultar contraseña', 'Show or hide password')}
            >
              {showPwd ? '🙈' : '👁️'}
            </span>
          </div>

          <label htmlFor="repeatPassword" style={S.label}>{copy('Repetí la contraseña', 'Repeat password')}</label>
          <input
            id="repeatPassword"
            name="new-password"
            type={showPwd ? 'text' : 'password'}
            autoComplete="new-password"
            style={S.input}
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            disabled={!token || loading}
            required
          />

          <button
            type="submit"
            style={{ ...S.submit, opacity: loading ? 0.7 : 1 }}
            disabled={!token || loading}
          >
            {loading ? copy('Actualizando…', 'Updating...') : copy('Actualizar contraseña', 'Update password')}
          </button>
        </form>
      </div>
    </div>
  );
};

const S = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(1200px 600px at -20% -20%, rgba(218,165,32,0.20), transparent), radial-gradient(1200px 600px at 120% 120%, rgba(75,0,130,0.18), transparent), url("/logo/Zodiacus.jpeg") center/cover no-repeat',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: 18,
    padding: 20,
    boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
    color: '#2a2150',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '80px 1fr 80px',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  backBtn: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid #e0e0e0',
    background: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    color: '#4B0082',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    color: '#DAA520',
    fontSize: 'clamp(1.2rem, 5.5vw, 1.6rem)',
  },
  alert: {
    padding: '10px 12px',
    borderRadius: 10,
    margin: '10px 0',
    fontSize: 14,
  },
  error: {
    background: '#fdecea',
    color: '#b3261e',
    padding: '10px 12px',
    borderRadius: 10,
    margin: '10px 0',
    fontSize: 14,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 },
  label: { fontWeight: 600, fontSize: 14 },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 12,
    border: '2px solid #4B0082',
    fontSize: 16,
    outline: 'none',
    background: '#fff',
  },
  toggle: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    fontSize: 18,
  },
  submit: {
    marginTop: 6,
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: 'linear-gradient(135deg, #DAA520, #f4c84a)',
    color: '#4B0082',
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(218,165,32,0.35)',
  },
};

export default ResetPassword;
